import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import take from 'lodash/take';
import { useHistory } from 'react-router-dom';

import Tag from '../V2/Tag';
import Money from 'core/api/Money';
import { ReactComponent as FreezeSVG } from 'core/assets/images/V2/icons/FreezeIcon.svg';
import { PRODUCT_TEMP } from 'core/assets/images/V2';
import { Box, Button, Flex, Image, Radio } from 'core/ui/atoms';
import { productDescriptionPath } from 'onramp/api/navigation';
import ResponsiveView from 'core/ui/components/ResponsiveView';

const MealItem = ({
  item,
  baseMealItem,
  isSelected,
  deliveryDayId,
  isSingleMeal,
  onSelectedMealChange,
}) => {
  const { dietTags, name, freezerFriendly, cost, images } = item;
  const history = useHistory();

  const RenderTags = () => {
    if (isEmpty(dietTags)) return null;
    return take(dietTags, 3)?.map((tag, index) => <Tag text={tag} key={index} />);
  };

  const RenderContainer = ({ children }) => {
    if (isSelected) {
      return <SelectedProductContainer>{children}</SelectedProductContainer>;
    } else {
      return <ProductContainer>{children}</ProductContainer>;
    }
  };

  const mealSize = item?.variants.find((variant) => variant.id === item.variantId)?.displayName;

  const getAmount = () => {
    const baseMealAmount = baseMealItem?.cost?.amount;
    const costAmount = cost?.amount;

    if (costAmount > baseMealAmount) {
      const differenceAmount = {
        __typename: 'Money',
        amount: costAmount - baseMealAmount,
      };
      return `(+${Money(differenceAmount).toFormat('$0,0')})`;
    } else if (baseMealAmount > costAmount) {
      const creditAmount = {
        __typename: 'Money',
        amount: baseMealAmount - costAmount,
      };
      return `(Credit ${Money(creditAmount).toFormat('$0,0')})`;
    }
  };

  const onHandleDetails = () => {
    history.push(productDescriptionPath(deliveryDayId, item?.id));
  };

  const imageURL = images?.[0]?.smallUrl || PRODUCT_TEMP;
  const largeImageURL = images?.[0]?.largeUrl || PRODUCT_TEMP;

  return (
    <RenderContainer>
      <ItemContainer direction="row" justifyContent="start">
        <SelectCont>
          <ResponsiveView desktop={false}>
            <ProductImage src={imageURL} alt={name} />
          </ResponsiveView>
          <ResponsiveView desktop={true}>
            <LargeProductImage src={largeImageURL} alt={name} />
          </ResponsiveView>
          {freezerFriendly && (
            <FreezeBadge>
              <FreezeSVG />
            </FreezeBadge>
          )}
        </SelectCont>
        <DetailsContainer>
          <ProductName>{name}</ProductName>
          <DietContainer direction="row" gap="3px">
            <RenderTags />
          </DietContainer>
          <ResponsiveView desktop={false}>
            <TagContainer>
              <Tag text={isSingleMeal ? 'One Size' : mealSize} style="outline" />
            </TagContainer>
          </ResponsiveView>
        </DetailsContainer>
      </ItemContainer>
      <DetailSection alignItems="center" justifyContent="space-between">
        <AmountCheck
          name={item?.id}
          value="0"
          label={getAmount()}
          checked={isSelected}
          onChange={onSelectedMealChange}
        />
        <ViewDetailButton onClick={onHandleDetails}>View details</ViewDetailButton>
      </DetailSection>
    </RenderContainer>
  );
};

MealItem.propTypes = {
  item: PropTypes.object.isRequired,
  baseMealItem: PropTypes.object,
  onSelectedMealChange: PropTypes.func,
  isSelected: PropTypes.bool,
  isSingleMeal: PropTypes.bool,
  deliveryDayId: PropTypes.string.isRequired,
};

const AmountCheck = styled(Radio)`
  width: 85%;
  justify-self: flex-start;
  ${(p) => p.theme.max('sm')`
    width: auto;
    justify-self: unset;
  `}
`;

const DietContainer = styled(Flex)`
  justify-content: center;
  margin-top: 8px;
  ${(p) => p.theme.max('sm')`
    justify-content: flex-start;
    margin-top: 0px;
  `}
`;

const ItemContainer = styled(Flex)`
  width: 100%;
  flex-direction: column;

  ${(p) => p.theme.max('sm')`
    flex-direction: row;
    padding-right: 0px;
    padding-left: 0px;
    padding-bottom: 20px;
  `}
`;

const TagContainer = styled(Box)`
  margin-top: 8px;
`;

const ViewDetailButton = styled(Button)`
  width: 100px;
  font-size: 12px;
  padding: 0px;
  text-decoration: underline;
  text-underline-offset: 5px;
  text-transform: uppercase;
`;

const DetailSection = styled(Flex)`
  flex-direction: column-reverse;
  row-gap: 20px;
  margin-top: 20px;
  width: 100%;
  ${(p) => p.theme.max('sm')`
    flex-direction: row;
    row-gap: unset;
    margin-top: unset;
  `}
`;

const ProductImage = styled(Image)`
  height: 94px;
  width: 94px;
  object-fit: contain;
`;

const LargeProductImage = styled(Image)`
  height: 238px;
  width: 238px;
  object-fit: contain;
`;

const SelectCont = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 10px;
  &:last-child {
    margin-right: 0;
  }
  ${(p) => p.theme.max('sm')`
    width: 28%;
    justify-content: end;
  `}
`;

const FreezeBadge = styled(Box)`
  height: 20px;
  width: 45px;
  border-radius: 4px;
  font-size: 12px;
  text-align: center;
  position: absolute;
  bottom: 0rem;
  left: 0rem;
`;

const SelectedProductContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 10px;
  border-radius: 4px;
  background: rgba(197, 209, 192, 0.24);
`;

const ProductContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 10px;
  border-radius: 4px;
`;

const DetailsContainer = styled(Box)`
  width: 100%;
  ${(p) => p.theme.max('sm')`
    width: 67%;
  `}
`;

const ProductName = styled.p`
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 16px;
  line-height: 22px;
  margin: 0px;
  text-align: center;
  margin-top: 10px;
  color: ${(p) => p.theme.colors.primaryV2};
  ${(p) => p.theme.max('sm')`
    text-align: unset;
    margin-top: unset;
  `}
`;

export default MealItem;
